import { authHeaders } from '../apiService';
import { ClassificationContext, classificationApiPrefix } from './transport';
import { digest, MUTATION_PREFIX, retainedMutation } from './bridge';

export type SavedRequest = {
  command_id: string; revision: string; phase: string;
  can_stop?: boolean; can_abandon?: boolean; job_id?: string;
  response_sha256?: string; response_byte_length?: number;
};

function selector(id: string) {
  if (!/^[a-f0-9]{64}$/.test(id)) throw new Error('Invalid saved request reference.');
  return `command_id=${encodeURIComponent(id)}`;
}

async function request(base: string, context: ClassificationContext, path: string, signal: AbortSignal, init: RequestInit = {}) {
  const headers = await authHeaders();
  if (!headers.Authorization) throw new Error('Sign in again to inspect saved requests.');
  const combined = new Headers(headers);
  new Headers(init.headers).forEach((value, name) => combined.set(name, value));
  return fetch(`${base}${classificationApiPrefix(context)}/commands/${path}`, {
    ...init, headers: combined, signal,
    credentials: 'omit', cache: 'no-store', redirect: 'error',
  });
}

async function clearVerifiedId(commandId: string, signal: AbortSignal) {
  // Storage holds only opaque operation IDs under hashed request keys. Server
  // listing is authoritative and still works in a new browser without them.
  const keys: string[] = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index); if (key?.startsWith(MUTATION_PREFIX)) keys.push(key);
  }
  for (const key of keys) {
    const id = sessionStorage.getItem(key);
    if (id && await digest(id) === commandId && !signal.aborted && sessionStorage.getItem(key) === id) sessionStorage.removeItem(key);
  }
}

export async function pendingRequests(base: string, context: ClassificationContext, signal: AbortSignal): Promise<SavedRequest[]> {
  const result = await request(base, context, 'pending', signal);
  if (!result.ok) throw new Error('Saved request status could not be loaded.');
  const body = await result.json();
  if (!Array.isArray(body.commands) || body.commands.some((row: SavedRequest) => !/^[a-f0-9]{64}$/.test(row.command_id)
    || typeof row.revision !== 'string' || !['pending', 'pending_job'].includes(row.phase))) throw new Error('Saved request references could not be verified.');
  const commands = new Map<string, SavedRequest>(body.commands.map((row: SavedRequest) => [row.command_id, row]));
  const local = new Set<string>();
  try {
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(MUTATION_PREFIX)) {
        const id = sessionStorage.getItem(key); if (id && /^[a-f0-9-]{20,64}$/.test(id)) local.add(id);
      }
    }
  } catch { /* Server discovery remains available when local storage is disabled. */ }
  // Only the actor/project-authorized server status can associate an opaque
  // local ID with this view. A terminal response may still await byte delivery.
  for (const id of Array.from(local)) {
    const commandId = await digest(id);
    if (commands.has(commandId)) continue;
    const status = await request(base, context, `status?mutation_id=${encodeURIComponent(id)}`, signal);
    if (status.status === 404 || status.status === 403) continue;
    if (!status.ok) throw new Error('Saved request status could not be loaded.');
    const row = await status.json();
    if (typeof row.revision !== 'string' || !['pending', 'pending_job', 'completed', 'rejected'].includes(row.phase)) {
      throw new Error('Saved request references could not be verified.');
    }
    commands.set(commandId, { ...row, command_id: commandId });
  }
  return Array.from(commands.values());
}

export async function checkSavedRequest(base: string, context: ClassificationContext, saved: SavedRequest, signal: AbortSignal): Promise<SavedRequest> {
  const status = await request(base, context, `status?${selector(saved.command_id)}`, signal);
  if (!status.ok) throw new Error('The saved request outcome is unavailable. Its operation ID is retained.');
  const current: SavedRequest = { ...await status.json(), command_id: saved.command_id };
  if (!['completed', 'rejected'].includes(current.phase)) return current;
  const result = await request(base, context, `result?${selector(saved.command_id)}`, signal);
  // A retained rejection legitimately has an HTTP error status. Verify its
  // exact bytes against the separately authorized terminal status receipt.
  const bytes = await result.arrayBuffer();
  if (signal.aborted || bytes.byteLength !== current.response_byte_length
    || await digest(bytes) !== current.response_sha256) throw new Error('The saved response was incomplete. Its operation ID is retained.');
  const terminal = result.headers.get('x-classification-command-status');
  if (terminal !== current.phase) throw new Error('The saved response status could not be verified.');
  if (signal.aborted) throw new Error('Saved request inspection was interrupted.');
  try { await clearVerifiedId(saved.command_id, signal); } catch { /* Server recovery also works with unavailable browser storage. */ }
  return current;
}

export async function stopSavedRequest(base: string, context: ClassificationContext, saved: SavedRequest, signal: AbortSignal) {
  if (!saved.can_stop && !saved.can_abandon) throw new Error('Check the saved outcome before stopping this request.');
  const body = new TextEncoder().encode(JSON.stringify({ command_id: saved.command_id, expected_revision: saved.revision })).buffer;
  const headers = { 'content-type': 'application/json' };
  const retained = await retainedMutation(context, { id: crypto.randomUUID(), path: '/api/commands/stop', method: 'POST', body }, headers);
  const result = await request(base, context, 'stop', signal, { method: 'POST', body,
    headers: { ...headers, 'X-Classification-Mutation-Id': retained.id } });
  await result.arrayBuffer();
  // Also reconcile the explicit stop command via its own immutable response;
  // a lost POST response is never permission to issue a fresh automatic POST.
  await checkSavedRequest(base, context, { command_id: await digest(retained.id), revision: '', phase: 'pending' }, signal);
  if (!result.ok) throw new Error('The saved request changed or could not be stopped. Check its outcome again.');
  return checkSavedRequest(base, context, saved, signal);
}
